import type React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { SocialIcon } from '@change/ds-core-ui';
import { FacebookRegularIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { useI18n } from '@change-corgi/core-react/i18n';

type Props = {
	caption: string;
	petition: { ask: string; photo?: string };
	/**
	 * The `variant_2` destination step's size: the same figure at about three quarters of the size, as
	 * the design draws it there, because the step also holds the two destination cards. On short
	 * viewports it caps the frame's height instead of scaling down further, so the caption loses lines
	 * and stays readable.
	 */
	compact?: boolean;
};

/**
 * A mock of the finished Facebook post, drawn from the prototype's `fb-post-preview.png` (375x726)
 * at the 210px width the prototype renders it: a frame with the Facebook badge on its corner, the
 * caption, then the link card Facebook builds from the petition's share link (photo on the left, the
 * brand-red title panel on the right). The frame ends at the link card. It approximates Facebook's own
 * preview, since the composer renders the real one.
 *
 * The frame is a 0.56-scale miniature, so its text sizes are the PNG's, scaled, rather than DS sizes:
 * the smallest DS text does not fit the link card's bottom row. Its spacing stays on DS steps.
 *
 * On short viewports the whole figure scales down in steps, so the sheet fits a 375x553 Safari viewport
 * with no scroll. The scale is a `scale` transform, not `zoom`: iOS Safari keeps zoomed text above its
 * minimum font size while the box shrinks, so a zoomed caption overflows its frame. A transform does
 * not shrink the layout box, so a wrapper takes the figure's scaled size, measured after layout.
 *
 * The compact figure scales once, at 760px, and below 760px caps the frame's height in steps: the caption
 * shrinks and is cut off, and the link card stays whole. Each cap leaves room for the question, the two
 * destination cards and their gaps (about 228px of the sheet body) at the bottom of its range. Below
 * 620px the cap stops at three caption lines, so the step scrolls a little there.
 *
 * The height ranges do not overlap, because Tailwind does not order arbitrary media variants by size.
 */
/**
 * The element's size after its transform, kept current through layout changes (a caption that wraps
 * differently) and viewport changes (a media query that changes the scale, which no layout change
 * reports). Measured before paint, so the first frame already has the scaled size.
 */
function useScaledSize(ref: React.RefObject<HTMLElement | null>) {
	const [size, setSize] = useState<{ width: number; height: number } | null>(null);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return undefined;
		const measure = () => {
			const { width, height } = element.getBoundingClientRect();
			setSize((previous) => (previous?.width === width && previous.height === height ? previous : { width, height }));
		};
		measure();
		const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
		observer?.observe(element);
		window.addEventListener('resize', measure);
		return () => {
			observer?.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [ref]);

	return size;
}

export const CaptionPreview: React.FC<Props> = ({ caption, petition, compact = false }) => {
	const { translate } = useI18n();
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const figureRef = useRef<HTMLElement>(null);
	const scaledSize = useScaledSize(figureRef);

	return (
		<div className="mx-auto" style={scaledSize ?? undefined}>
			{/* The padding is the PNG's margin around the frame, which leaves room for the badge. */}
			<figure
				ref={figureRef}
				className={cn(
					'my-0 w-[210px] origin-top-left pt-5 pr-7 pb-0.5 pl-0.5',
					compact
						? '[scale:0.74] [@media(max-height:760px)]:[scale:0.62]'
						: '[@media(max-height:560px)]:[scale:0.62] [@media(min-height:560.02px)_and_(max-height:620px)]:[scale:0.78] [@media(min-height:620.02px)_and_(max-height:700px)]:[scale:0.9]',
				)}
				aria-label={translate('corgi.psf.promote_or_share.facebook_post_sheet.preview_label')}
				data-testid="facebook-post-sheet-preview"
			>
				{/* The badge sits outside the frame, so the frame's clip does not cut it. */}
				<div className="relative">
					{/* Decorative: the dialog's title already says Facebook. */}
					<span
						aria-hidden="true"
						className="absolute -top-2.5 -right-2.5 z-10"
						data-testid="facebook-post-sheet-preview-badge"
					>
						<SocialIcon size="medium" variant="facebook" icon={<FacebookRegularIcon />} />
					</span>
					<div
						className={cn(
							'border-stroke-weaker flex flex-col overflow-hidden rounded-3xl border-2',
							compact &&
								'[@media(max-height:620px)]:max-h-[181px] [@media(min-height:620.02px)_and_(max-height:660px)]:max-h-[218px] [@media(min-height:660.02px)_and_(max-height:700px)]:max-h-[283px] [@media(min-height:700.02px)_and_(max-height:760px)]:max-h-[347px]',
						)}
					>
						{/* A long caption is cut off at 17 lines, and the card never shrinks, so it stays whole. */}
						<p className="text-typography-strong line-clamp-17 min-h-0 shrink px-2.5 pt-9 text-[9px] leading-[13px] wrap-break-word whitespace-pre-line">
							{caption}
						</p>
						<div className="mt-1.5 flex h-24 shrink-0">
							<img
								alt=""
								className="block h-full w-[56%] object-cover"
								src={petition.photo ?? getStaticAssetUrl('/images/default-petition-darker.gif')}
							/>
							<div className="bg-fill-brand-strong text-typography-inverse-strong-fixed flex min-w-0 flex-1 flex-col justify-between p-2">
								<p className="line-clamp-4 text-[9px] leading-[11px] font-bold">{petition.ask}</p>
								<div className="flex items-center justify-between gap-0.5">
									<span className="bg-fill-strong text-typography-inverse-strong rounded-full px-1 py-0.5 text-[4px] leading-none font-bold whitespace-nowrap">
										{translate('corgi.psf.promote_or_share.facebook_post_sheet.link_card_cta')}
									</span>
									<span className="text-[5px] leading-none font-extrabold whitespace-nowrap">
										{translate('corgi.psf.promote_or_share.facebook_post_sheet.link_card_domain')}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</figure>
		</div>
	);
};
